import React, { Component } from 'react';
import ReactMarkdown from 'react-markdown';
import { OBDAPI } from '../../services/obdService';

// Friendly labels for tool_start/tool_complete events
const TOOL_LABELS = {
  get_revenue_summary: 'Pulling revenue data',
  get_outstanding_receivables: 'Looking up receivables',
  get_revenue_by_dealer: 'Analyzing dealer revenue',
  get_revenue_by_service: 'Breaking down service revenue',
  get_estimate_accuracy: 'Checking estimate accuracy',
  get_dealer_concentration: 'Analyzing dealer concentration',
  get_dealer_trends: 'Reviewing dealer trends',
  get_unconverted_estimates: 'Finding unconverted estimates',
  get_jobs_summary: 'Summarizing jobs',
  get_estimate_conversion_rate: 'Calculating conversion rates',
  get_customer_repeat_rate: 'Checking customer repeat rate',
  get_inactive_customers: 'Finding inactive customers',
  get_upcoming_schedule: 'Checking upcoming schedule',
  get_schedule_capacity: 'Analyzing schedule capacity',
  get_seasonality_pattern: 'Looking at seasonal patterns',
  get_material_cost_per_job: 'Calculating material costs',
  get_product_usage: 'Reviewing product usage',
  get_dealer_location_comparison: 'Comparing dealer locations',
  get_dealer_location_submitters: 'Identifying top submitters',
  get_dealer_vehicle_mix: 'Analyzing vehicle mix',
  get_dealer_location_trend: 'Checking location trends',
  get_revenue_per_labor_hour: 'Computing revenue per labor hour',
  get_labor_gap_analysis: 'Analyzing labor gaps',
  get_cross_sell_opportunities: 'Finding cross-sell opportunities',
  get_quote_decay: 'Analyzing quote aging'
};

// Map chip icon strings to FA Sharp icons
const CHIP_ICONS = {
  store: 'fa-sharp fa-solid fa-store',
  target: 'fa-sharp fa-solid fa-bullseye',
  dollar: 'fa-sharp fa-solid fa-dollar-sign',
  calendar: 'fa-sharp fa-solid fa-calendar',
  alert: 'fa-sharp fa-solid fa-triangle-exclamation',
  'trending-down': 'fa-sharp fa-solid fa-arrow-trend-down'
};

class OBDPanel extends Component {
  constructor(props) {
    super(props);
    this.state = {
      messages: [],
      chips: [],
      sessionId: null,
      inputValue: '',
      isStreaming: false,
      streamingText: '',
      currentTools: [],
      initialized: false,
      loading: false,
      error: null,
      chipDrawerOpen: true
    };
    this.messagesEndRef = React.createRef();
  }

  componentDidUpdate(prevProps) {
    // Initialize on open
    if (this.props.show && !prevProps.show && !this.state.initialized) {
      this.initialize();
    }
    // Reset session when panel closes (conversation is done)
    if (!this.props.show && prevProps.show) {
      this.setState({
        messages: [],
        chips: [],
        sessionId: null,
        initialized: false,
        isStreaming: false,
        streamingText: '',
        currentTools: [],
        error: null,
        chipDrawerOpen: true
      });
    }
    // Reset session when location changes (data context changed)
    if (this.props.locationId !== prevProps.locationId && this.state.initialized) {
      this.setState({
        messages: [],
        chips: [],
        sessionId: null,
        initialized: false,
        isStreaming: false,
        streamingText: '',
        currentTools: [],
        error: null,
        chipDrawerOpen: true
      }, () => {
        if (this.props.show) this.initialize();
      });
    }
    this.scrollToBottom();
  }

  scrollToBottom = () => {
    if (this.messagesEndRef.current) {
      const container = this.messagesEndRef.current.parentElement;
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
    }
  };

  initialize = async () => {
    const sessionId = 'obd-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9);
    this.setState({ initialized: true, loading: true, sessionId });

    // Load chips
    try {
      const data = await OBDAPI.getChips(this.props.installerId, this.props.locationId);
      this.setState({ chips: data.chips || [] });
    } catch (err) {
      console.error('[OBD] Failed to load chips:', err);
    }

    // Send kickoff (empty message)
    await this.sendMessageStreaming('', sessionId);
    this.setState({ loading: false });
  };

  // --- SSE Streaming (adapted from EstimateChatbot) ---

  parseSSEEvents(buffer) {
    const events = [];
    const normalizedBuffer = buffer.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
    const chunks = normalizedBuffer.split('\n\n');
    const remaining = chunks.pop() || '';

    for (const chunk of chunks) {
      if (!chunk.startsWith('data: ')) continue;
      try {
        const jsonStr = chunk.slice(6);
        const data = JSON.parse(jsonStr);
        if (data.type) {
          events.push({ type: data.type, data });
        }
      } catch (e) {
        console.warn('[OBD] Failed to parse SSE chunk:', chunk, e);
      }
    }
    return { events, remaining };
  }

  async sendMessageStreaming(message, overrideSessionId) {
    const sessionId = overrideSessionId || this.state.sessionId;

    // Add placeholder bot message
    let placeholderIndex;
    await new Promise(resolve => {
      this.setState(prevState => {
        placeholderIndex = prevState.messages.length;
        return {
          messages: [...prevState.messages, { text: '', type: 'bot', timestamp: new Date(), isStreaming: true }],
          isStreaming: true,
          streamingText: '',
          currentTools: []
        };
      }, resolve);
    });

    try {
      const { url, token } = await OBDAPI.getStreamConfig();

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token
        },
        body: JSON.stringify({
          message: message || '',
          sessionId,
          installerId: this.props.installerId,
          locationId: this.props.locationId || null
        })
      });

      if (!response.ok) {
        throw new Error(`Stream request failed: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let accumulatedText = '';
      const msgIndex = placeholderIndex;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const { events, remaining } = this.parseSSEEvents(buffer);
        buffer = remaining;

        for (const event of events) {
          switch (event.type) {
            case 'thinking':
              break;

            case 'model':
              break;

            case 'delta':
              if (event.data && event.data.text) {
                accumulatedText += event.data.text;
                const currentText = accumulatedText;
                this.setState(prevState => {
                  const messages = [...prevState.messages];
                  messages[msgIndex] = { ...messages[msgIndex], text: currentText };
                  return { messages, streamingText: currentText };
                });
              }
              break;

            case 'tool_start':
              if (event.data && event.data.tool) {
                this.setState(prevState => ({
                  currentTools: [...prevState.currentTools, event.data.tool]
                }));
              }
              break;

            case 'tool_complete':
              if (event.data && event.data.tool) {
                this.setState(prevState => ({
                  currentTools: prevState.currentTools.filter(t => t !== event.data.tool)
                }));
              }
              break;

            case 'done':
              if (event.data) {
                if (event.data.sessionId) {
                  this.setState({ sessionId: event.data.sessionId });
                }
                const finalText = accumulatedText || '(no response)';
                this.setState(prevState => {
                  const messages = [...prevState.messages];
                  messages[msgIndex] = {
                    ...messages[msgIndex],
                    text: finalText,
                    isStreaming: false
                  };
                  return {
                    messages,
                    isStreaming: false,
                    streamingText: '',
                    currentTools: []
                  };
                });
              }
              break;

            case 'error':
              throw new Error(event.data?.message || 'Stream error');

            default:
              break;
          }
        }
      }

      // If stream ended without done event, finalize
      if (this.state.isStreaming) {
        this.setState(prevState => {
          const messages = [...prevState.messages];
          messages[placeholderIndex] = {
            ...messages[placeholderIndex],
            text: accumulatedText || '(no response)',
            isStreaming: false
          };
          return { messages, isStreaming: false, streamingText: '', currentTools: [] };
        });
      }

      return true;
    } catch (error) {
      console.error('[OBD] Streaming failed:', error);

      // Remove placeholder and fall back to non-streaming
      this.setState(prevState => ({
        messages: prevState.messages.filter((_, idx) => idx !== placeholderIndex),
        isStreaming: false,
        streamingText: '',
        currentTools: []
      }));

      return this.sendMessageFallback(message, sessionId);
    }
  }

  async sendMessageFallback(message, sessionId) {
    try {
      const data = await OBDAPI.sendMessage(message, sessionId || this.state.sessionId, this.props.installerId, this.props.locationId);
      this.setState(prevState => ({
        messages: [...prevState.messages, { text: data.message || '(no response)', type: 'bot', timestamp: new Date() }]
      }));
      if (data.sessionId) {
        this.setState({ sessionId: data.sessionId });
      }
      return true;
    } catch (err) {
      console.error('[OBD] Fallback chat failed:', err);
      this.setState({ error: 'Unable to reach OBD. Please try again.' });
      return false;
    }
  }

  // --- User Actions ---

  handleInputChange = (e) => {
    this.setState({ inputValue: e.target.value });
  };

  handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      this.handleSend();
    }
  };

  handleSend = async () => {
    const message = this.state.inputValue.trim();
    if (!message || this.state.isStreaming) return;

    this.setState(prevState => ({
      inputValue: '',
      chipDrawerOpen: false,
      messages: [...prevState.messages, { text: message, type: 'user', timestamp: new Date() }]
    }));

    await this.sendMessageStreaming(message);
  };

  handleChipClick = async (chip) => {
    if (this.state.isStreaming) return;

    this.setState(prevState => ({
      chipDrawerOpen: false,
      messages: [...prevState.messages, { text: chip.label, type: 'user', timestamp: new Date() }]
    }));

    await this.sendMessageStreaming(chip.label);
  };

  // --- Render ---

  renderToolIndicator() {
    const { currentTools } = this.state;
    if (currentTools.length === 0) return null;

    const toolName = currentTools[currentTools.length - 1];
    const label = TOOL_LABELS[toolName] || 'Analyzing data';

    return (
      <div className="obd-tool-indicator">
        <i className="fa-sharp fa-solid fa-gear" />
        <span>{label}...</span>
      </div>
    );
  }

  toggleChipDrawer = () => {
    this.setState(prev => ({ chipDrawerOpen: !prev.chipDrawerOpen }));
  };

  renderChipDrawer() {
    const { chips, chipDrawerOpen } = this.state;
    if (chips.length === 0) return null;

    return (
      <div className={`obd-chip-drawer ${chipDrawerOpen ? 'open' : ''}`}>
        {chips.map(chip => (
          <button
            key={chip.id}
            className="obd-chip"
            onClick={() => this.handleChipClick(chip)}
            disabled={this.state.isStreaming}
          >
            <i className={CHIP_ICONS[chip.icon] || 'fa-sharp fa-solid fa-circle-question'} />
            {chip.label}
          </button>
        ))}
      </div>
    );
  }

  renderMessages() {
    const { messages } = this.state;

    return messages.map((msg, i) => {
      if (msg.type === 'user') {
        return (
          <div key={i} className="obd-msg obd-msg-user">
            {msg.text}
          </div>
        );
      }

      return (
        <div key={i} className="obd-msg obd-msg-bot">
          {msg.text ? (
            <ReactMarkdown>{msg.text}</ReactMarkdown>
          ) : (
            <div className="typing-indicator">
              <span></span><span></span><span></span>
            </div>
          )}
          {msg.isStreaming && msg.text && (
            <span className="streaming-cursor">|</span>
          )}
        </div>
      );
    });
  }

  render() {
    const { show, onClose } = this.props;
    const { isStreaming, inputValue, loading, error } = this.state;

    return (
      <div className={`obd-panel ${show ? 'open' : ''}`}>
        {/* Header */}
        <div className="obd-header">
          <div className="obd-header-title">
            <i className="fa-sharp fa-solid fa-engine-warning" />
            <span>OBD</span>
            <span className="obd-header-subtitle">On-Board Diagnostics</span>
          </div>
          <button className="obd-header-close" onClick={onClose}>
            <i className="fa-sharp fa-solid fa-xmark" />
          </button>
        </div>

        {/* Messages */}
        <div className="obd-messages">
          {loading && !this.state.messages.length && (
            <div className="obd-msg obd-msg-bot">
              <div className="typing-indicator">
                <span></span><span></span><span></span>
              </div>
            </div>
          )}

          {this.renderMessages()}
          {this.renderToolIndicator()}

          <div ref={this.messagesEndRef} />
        </div>

        {/* Error */}
        {error && (
          <div className="obd-error">
            <i className="fa-sharp fa-solid fa-circle-exclamation mr-1" />
            {error}
          </div>
        )}

        {/* Chip drawer (slides up above input) */}
        {this.renderChipDrawer()}

        {/* Input */}
        <div className="obd-input-area">
          {this.state.chips.length > 0 && (
            <button
              className={`obd-chip-toggle ${this.state.chipDrawerOpen ? 'active' : ''}`}
              onClick={this.toggleChipDrawer}
              title={this.state.chipDrawerOpen ? 'Hide suggestions' : 'Show suggestions'}
              disabled={isStreaming}
            >
              <i className="fa-sharp fa-solid fa-lightbulb" />
            </button>
          )}
          <input
            className="obd-input"
            type="text"
            placeholder="Ask OBD anything..."
            value={inputValue}
            onChange={this.handleInputChange}
            onKeyPress={this.handleKeyPress}
            disabled={isStreaming || loading}
          />
          <button
            className="obd-send-btn"
            onClick={this.handleSend}
            disabled={!inputValue.trim() || isStreaming || loading}
          >
            <i className="fa-sharp fa-solid fa-paper-plane-top" />
          </button>
        </div>
      </div>
    );
  }
}

export default OBDPanel;
